import { Injectable, Injector } from "@angular/core";
import { Zone } from "@common/ADAPT.Common.Model/methodology/zone";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { TeamLocation, TeamLocationBreezeModel } from "@common/ADAPT.Common.Model/organisation/team-location";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { IBreezeEntity } from "@common/lib/data/breeze-entity.interface";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { LocalStorage } from "@common/lib/storage/local-storage";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { BaseOrganisationService } from "@org-common/lib/organisation/base-organisation.service";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { TeamHierarchyService } from "@org-common/lib/teams/team-hierarchy.service";
import { BehaviorSubject, lastValueFrom, merge, Subject } from "rxjs";
import { debounceTime, map, startWith, switchMap, tap } from "rxjs/operators";
import { Options as SortablejsOptions, SortableEvent } from "sortablejs";

@Injectable({
    providedIn: "root",
})
export class OrganisationMapService extends BaseOrganisationService {
    public readonly defaultSortableGroup = "map";
    public get defaultSortableOptions(): SortablejsOptions {
        const testingFallback = LocalStorage.get<boolean>("orgMapTestingFallback") === true;
        return {
            animation: 150,
            disabled: false,
            handle: ".dx-list-reorder-handle",
            // exclude inactive cards from sortable indices.
            // Inactive parents may also contain active children with their own handles.
            draggable: "> adapt-team-card:not(.inactive-team)",
            group: {
                name: this.defaultSortableGroup,
                pull: true,
                put: (to, from, dragEl) => this.canDropTeam(to.el, from.el, dragEl),
            },
            revertOnSpill: true,
            swapThreshold: 0.20,
            emptyInsertThreshold: 0,
            forceFallback: testingFallback,
            scroll: !testingFallback,
            forceAutoScrollFallback: true,
            onRemove: this.handleMapSortableMove,
            onUpdate: this.handleMapSortableUpdate,
            onChoose: this.handleMapSortableChoose,
            onUnchoose: this.handleMapSortableUnchoose,
        };
    }

    private mapEntityChangedSubject = new Subject<IBreezeEntity[]>();
    public mapEntityChanged$ = this.mapEntityChangedSubject.asObservable();

    private mapEntityDraggingSubject = new BehaviorSubject<Team | undefined>(undefined);
    public mapEntityDragging$ = this.mapEntityDraggingSubject.asObservable();

    public readonly hierarchyChanged$ = new Subject<void>();
    private allTeamLocations: TeamLocation[] = [];

    private teamLocations = new Map<number, TeamLocation[]>();
    private teamIdsWithChildren = new Set<number>();
    private activeTeamIdsWithChildren = new Set<number>();

    private sourceZone?: Zone;
    private sourceParentTeamId?: number;

    constructor(
        injector: Injector,
        private commonTeamsService: CommonTeamsService,
        private rxjsBreezeService: RxjsBreezeService,
    ) {
        super(injector);

        const teamChanges$ = merge(
            this.mapEntityChanged$,
            this.rxjsBreezeService.entityTypeChanged(Team),
            this.rxjsBreezeService.entityTypeChanged(TeamLocation),
            this.rxjsBreezeService.entityTypeAttached(TeamLocation),
        );

        this.organisation$.pipe(
            switchMap(() => teamChanges$.pipe(
                debounceTime(100),
                switchMap(() => this.commonTeamsService.getAllTeamLocations()),
            )),
        ).subscribe((teamLocations) => this.updateTeamLocationData(teamLocations));
    }

    protected organisationInitialisationActions() {
        return [
            this.commonTeamsService.getAllTeamLocations().pipe(
                tap((teamLocations) => this.updateTeamLocationData(teamLocations))
            ),
        ];
    }

    public emitWhenLocationShouldBeFetched(location: Partial<TeamLocation>) {
        return this.hierarchyChanged$.pipe(
            map(() => location),
            startWith(location),
        );
    }

    public getAncestorTeamIds(teamIds: Iterable<number>) {
        return TeamHierarchyService.getAncestorTeamIds(teamIds, this.allTeamLocations);
    }

    public getLocationItems(location: Partial<TeamLocation>, includeInactiveTeams = false) {
        return this.commonTeamsService.getLocationItems(location, true).pipe(
            map((locations) => locations.filter((loc) => includeInactiveTeams
                || (loc.team.isActive() || this.teamHasChildren(loc.teamId)))
                .sort(SortUtilities.getSortByFieldFunction<TeamLocation>(
                    (loc) => !loc.team.isActive(),
                    (loc) => loc.team.isActive() ? loc.ordinal : loc.team.name,
                ))),
        );
    }

    public addTeamToZone(team: Team, zone: Zone) {
        const loc = new TeamLocation();
        loc.team = team;
        loc.zone = zone;
        return this.commonTeamsService.getLocationItems(loc, true).pipe(
            switchMap((locations) => {
                // -1 as fallback for when no other locations present
                return this.commonDataService.create(TeamLocationBreezeModel, {
                    team,
                    zone,
                    ordinal: this.getNextOrdinal(locations),
                });
            }),
        );
    }

    public emitMapEntityChanged(entities?: IBreezeEntity[]) {
        this.mapEntityChangedSubject.next(entities ?? []);
    }

    public emitMapEntityDragging(team?: Team) {
        this.mapEntityDraggingSubject.next(team);
    }

    public teamHasChildren(teamId: number, includeInactiveTeams = false) {
        const teamIdsWithChildren = includeInactiveTeams
            ? this.teamIdsWithChildren
            : this.activeTeamIdsWithChildren;
        return teamIdsWithChildren.has(teamId);
    }

    /**
     * Determines whether a team can be dropped into the given location.
     *
     * A team cannot be dropped if:
     * - It already has a location in the destination zone, or
     * - It already has a location under the destination parent team.
     */
    public canDropTeam(to: HTMLElement, from: HTMLElement, dragEl: HTMLElement) {
        const {
            destinationParentTeamId,
            destinationZone,
            teamId,
        } = this.getTargetDetailsFromSortableEvent(from, to, dragEl);

        const teamLocations = this.teamLocations.get(teamId);
        if (!teamLocations) {
            return true;
        }

        // If destination is a zone, check if the team already has a location in that zone
        if (destinationZone) {
            const hasZoneLocation = teamLocations.some((loc) => loc.zone === destinationZone);
            if (hasZoneLocation) {
                return false;
            }
        }

        // If destination is a parent team, check if the team already has a location under it
        if (destinationParentTeamId) {
            const hasParentLocation = teamLocations.some((loc) => loc.parentTeamId === destinationParentTeamId);
            if (hasParentLocation) {
                return false;
            }
        }

        // Moving to global. Only allow when the team only has one location.
        if (!destinationZone && !destinationParentTeamId) {
            return teamLocations.length <= 1;
        }

        return true;
    }

    @Autobind
    public async handleMapSortableMove(event: SortableEvent) {
        // remove is called on the source when an item moves to another sortable list
        // indexes we need are not set
        if (event.oldDraggableIndex === undefined || event.newDraggableIndex === undefined) {
            return;
        }

        const {
            team,
            fromLocation,
            toLocation,
            sourceTeamLocation,
            sourceTeamLocations,
            destinationParentTeam,
            destinationZone,
        } = await this.getPopulatedTargetDetailsFromSortableEvent(event.from, event.to, event.item);

        if (fromLocation === toLocation) {
            throw new Error("fromLocation is toLocation for sortable move");
        }

        let destLocation: TeamLocation | undefined;

        if (destinationParentTeam) {
            destLocation = await lastValueFrom(this.commonTeamsService.addChildTeamForParentTeam(team, destinationParentTeam));
        } else if (destinationZone) {
            destLocation = await lastValueFrom(this.addTeamToZone(team, destinationZone));
        }

        // Compact active siblings; inactive locations do not participate in ordering.
        if (sourceTeamLocation) {
            ArrayUtilities.removeElementFromArray(sourceTeamLocation, sourceTeamLocations);
            SortUtilities.updateIntegerSortedArrayAfterItemRemoval(sourceTeamLocations, "ordinal");
            await lastValueFrom(this.commonDataService.remove(sourceTeamLocation));
        }

        // add to the new location and reorder
        let destLocations: TeamLocation[] = [];
        if (destLocation) {
            destLocations = await lastValueFrom(this.commonTeamsService.getLocationItems(destLocation));
            SortUtilities.reorderItemInIntegerSortedArray(destLocations, "ordinal", destLocations.indexOf(destLocation), event.newDraggableIndex);
        }

        // remove the dragged item from the destination, as the destination will refresh and add the element itself
        event.item.remove();

        const entities = ArrayUtilities.distinct(ArrayUtilities.withoutNullOrUndefined([...sourceTeamLocations, ...destLocations, sourceTeamLocation, destLocation]));
        this.mapEntityChangedSubject.next(entities);
    }

    @Autobind
    public async handleMapSortableUpdate(event: SortableEvent) {
        // update is called for when the order of items in a sortable list changes
        // indexes we need are not set
        if (event.oldDraggableIndex === undefined || event.newDraggableIndex === undefined) {
            return;
        }

        const {
            fromLocation,
            toLocation,
            sourceTeamLocations,
        } = await this.getPopulatedTargetDetailsFromSortableEvent(event.from, event.to, event.item);

        if (fromLocation !== toLocation) {
            throw new Error("fromLocation is not toLocation for sortable update");
        }

        SortUtilities.reorderItemInIntegerSortedArray(sourceTeamLocations, "ordinal", event.oldDraggableIndex, event.newDraggableIndex);
        this.mapEntityChangedSubject.next(sourceTeamLocations);
    }

    @Autobind
    public async handleMapSortableChoose(event: SortableEvent) {
        const {
            team,
            sourceTeamLocation,
        } = await this.getPopulatedTargetDetailsFromSortableEvent(event.from, event.to, event.item);
        if (sourceTeamLocation) {
            this.sourceZone = sourceTeamLocation.zone ?? undefined;
            this.sourceParentTeamId = sourceTeamLocation.parentTeamId;
        }
        this.emitMapEntityDragging(team);
    }

    @Autobind
    public async handleMapSortableUnchoose() {
        this.emitMapEntityDragging(undefined);
        this.sourceZone = undefined;
        this.sourceParentTeamId = undefined;
    }

    public draggingTeamCanBeDroppedIntoZone(zone: Zone): boolean {
        const team = this.mapEntityDraggingSubject.value;
        if (!team) {
            return true;
        }

        const teamLocations = this.teamLocations.get(team.teamId);
        if (!teamLocations) {
            return true;
        }

        return !teamLocations.some((loc) => loc.zone === zone && loc.zone !== this.sourceZone);
    }

    public draggingTeamCanBeDroppedIntoParentTeam(parentTeamId: number): boolean {
        const team = this.mapEntityDraggingSubject.value;
        if (!team) {
            return true;
        }

        const teamLocations = this.teamLocations.get(team.teamId);
        if (!teamLocations) {
            return true;
        }

        return !teamLocations.some((loc) => loc.parentTeamId === parentTeamId && loc.parentTeamId !== this.sourceParentTeamId);
    }

    private updateTeamLocationData(teamLocations: TeamLocation[]) {
        const locationsForTeams = new Map<number, TeamLocation[]>();
        const teamIdsWithChildren = new Set<number>();
        const activeTeamIdsWithChildren = TeamHierarchyService.getAncestorTeamIds(
            teamLocations.filter((loc) => loc.team?.isActive()).map((loc) => loc.teamId),
            teamLocations,
        );

        for (const teamLocation of teamLocations) {
            const locations = locationsForTeams.get(teamLocation.teamId) ?? [];
            locations.push(teamLocation);
            locationsForTeams.set(teamLocation.teamId, locations);
            if (teamLocation.parentTeamId) {
                teamIdsWithChildren.add(teamLocation.parentTeamId);
            }
        }

        this.allTeamLocations = teamLocations;
        this.teamLocations = locationsForTeams;
        this.teamIdsWithChildren = teamIdsWithChildren;
        this.activeTeamIdsWithChildren = activeTeamIdsWithChildren;
        this.hierarchyChanged$.next();
    }

    private getNextOrdinal(locations: TeamLocation[]) {
        return Math.max(-1, ...locations.map((i) => i.ordinal)) + 1;
    }

    private getTargetDetailsFromSortableEvent(fromElement: HTMLElement, toElement: HTMLElement, item: HTMLElement) {
        const fromLocation = fromElement.dataset.location;
        const toLocation = toElement.dataset.location;
        if (!fromLocation) {
            throw new Error("fromLocation is not defined");
        }

        if (!toLocation) {
            throw new Error("toLocation is not defined");
        }

        let teamId: number | undefined;
        const itemTeamId = item.dataset.teamId;
        if (itemTeamId) {
            teamId = parseInt(itemTeamId, 10);
        } else {
            throw new Error("teamId is not defined");
        }

        let sourcePartialTeamLocation: Partial<TeamLocation> | undefined;
        const [sourceType, sourceTypeId] = fromLocation.split("-");

        if (sourceType === "team") {
            sourcePartialTeamLocation = { parentTeamId: parseInt(sourceTypeId, 10) };
        } else if (sourceType === "zone") {
            sourcePartialTeamLocation = sourceTypeId !== "global"
                ? { zone: sourceTypeId as Zone }
                : undefined;
        }

        let destinationParentTeamId: number | undefined;
        let destinationZone: Zone | undefined;

        const [destinationType, destinationTypeId] = toLocation.split("-");
        if (destinationType === "team") {
            destinationParentTeamId = parseInt(destinationTypeId, 10);
        }

        if (destinationType === "zone" && destinationTypeId !== "global") {
            destinationZone = destinationTypeId as Zone;
        }

        return {
            teamId,
            fromLocation,
            toLocation,
            sourcePartialTeamLocation,
            destinationParentTeamId,
            destinationZone,
            sourceType,
            sourceTypeId,
            destinationType,
            destinationTypeId,
        };
    }

    // populate sortable fields that require async operations.
    // this is done separately so that we can get the targets in a non-async context
    private async getPopulatedTargetDetailsFromSortableEvent(fromElement: HTMLElement, toElement: HTMLElement, item: HTMLElement) {
        const {
            teamId,
            fromLocation,
            toLocation,
            sourcePartialTeamLocation,
            sourceTypeId,
            destinationParentTeamId,
            destinationZone,
        } = this.getTargetDetailsFromSortableEvent(fromElement, toElement, item);

        const team = await lastValueFrom(this.commonTeamsService.getTeamById(teamId));
        if (!team) {
            throw new Error(`team ${teamId} not found`);
        }

        const sourceTeamLocations = sourcePartialTeamLocation
            ? await lastValueFrom(this.commonTeamsService.getLocationItems(sourcePartialTeamLocation))
            : [];

        const sourceTeamLocation = sourceTeamLocations.find((loc) => loc.teamId === team.teamId);
        if (!sourceTeamLocation && sourceTypeId !== "global") {
            throw new Error(`No source location exists for ${team.teamId} not in global zone`);
        }

        let destinationParentTeam: Team | undefined;
        if (destinationParentTeamId) {
            const parentTeam = await lastValueFrom(this.commonTeamsService.getTeamById(destinationParentTeamId));
            if (!parentTeam) {
                throw new Error(`destination team ${destinationParentTeamId} was not found`);
            }

            destinationParentTeam = parentTeam;
        }

        return {
            team,
            fromLocation,
            toLocation,
            sourceTeamLocation,
            sourceTeamLocations,
            destinationParentTeam,
            destinationZone,
        };
    }
}
